import { Link } from "react-router-dom";
import { ArrowRight, Layers, ShieldCheck, PlugZap, FlaskConical, Rocket, LifeBuoy, AppWindow, Database, Bot, BarChart3, Users, Cog, FileBarChart, Smartphone, Cloud } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { useSite } from "@/lib/content";

const BUILD_ITEMS = [
  { icon: AppWindow, title: "Custom business applications", text: "Purpose-built systems that match your exact workflows." },
  { icon: Cloud, title: "SaaS platforms", text: "Subscription-ready products with multi-tenant architecture." },
  { icon: Cog, title: "Internal operations systems", text: "One system of record for how your business runs." },
  { icon: Users, title: "Customer portals", text: "Self-service experiences your customers will actually use." },
  { icon: BarChart3, title: "Administrative dashboards", text: "Visibility into the metrics that drive decisions." },
  { icon: Layers, title: "Workflow automation", text: "Approvals, routing and document handling on autopilot." },
  { icon: Database, title: "Database-driven applications", text: "Reliable data models at the heart of your operation." },
  { icon: PlugZap, title: "API integrations", text: "Your tools, connected and synchronized." },
  { icon: FileBarChart, title: "Reporting systems", text: "Accurate, timely reporting without manual assembly." },
  { icon: Smartphone, title: "Mobile-ready web applications", text: "Responsive experiences on every device." },
  { icon: Bot, title: "AI-enabled software", text: "Practical AI embedded where it creates real value." },
];

const SECTIONS = [
  {
    icon: Layers, title: "Software Architecture",
    text: "We design architecture that fits the size of your problem today and the size of your ambition tomorrow — clean separation of concerns, documented decisions, and foundations your future team will thank you for.",
  },
  {
    icon: ShieldCheck, title: "Security & Reliability",
    text: "Security is incorporated throughout development: secure authentication, access control, input validation, encrypted connections and responsible data handling — reviewed before every launch.",
  },
  {
    icon: PlugZap, title: "Integrations",
    text: "Modern software rarely stands alone. We build APIs, webhooks and data pipelines that connect your platform to the tools your business already depends on.",
  },
  {
    icon: FlaskConical, title: "Testing & Quality Assurance",
    text: "Functional, security, usability and performance testing are part of the build — not an afterthought. Every release is verified against the workflows it supports.",
  },
  {
    icon: Rocket, title: "Deployment",
    text: "We deploy to production environments with monitoring, backups and rollback plans in place, so launch day is a milestone — not a gamble.",
  },
  {
    icon: LifeBuoy, title: "Ongoing Support",
    text: "Software should keep improving after launch. We offer continued support, monitoring and iteration as your business and the platform evolve together.",
  },
];

export default function SoftwareDevelopment() {
  const { data } = useSite();
  const seo = data?.settings?.seo?.["/services/software-development"];

  return (
    <>
      <Seo
        title={seo?.title || "Custom Software Development | LogiTech Nexus"}
        siteName="LogiTech Nexus"
        description={seo?.description || "Custom software built for the way you work — business applications, SaaS platforms, portals, dashboards, integrations and AI-enabled software."}
      />
      <PageHero
        eyebrow="Custom Software Development"
        title="Custom Software. Built for"
        accent="the Way You Work."
        subtitle="Your business doesn't fit into a generic template — your software shouldn't either. We design and develop systems around your workflows, your customers and your growth plans."
        testid="swdev-hero"
      />

      <section className="bg-void py-24 sm:py-32" data-testid="swdev-what-we-build">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>What We Build</Eyebrow>
            <h2 className="mt-4 max-w-2xl font-display text-2xl font-semibold tracking-tight text-white sm:text-3xl lg:text-4xl">
              If your business runs on it, we can build it.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {BUILD_ITEMS.map((item, i) => (
              <Reveal key={item.title} delay={Math.min(i * 0.05, 0.4)} y={20}>
                <div className="group h-full rounded-2xl border border-slate-800/80 bg-navy/60 p-7 transition-[border-color,transform] duration-500 hover:-translate-y-1 hover:border-blue-500/40" data-testid={`swdev-item-${i}`}>
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-blue-500/25 bg-blue-500/10 text-blue-400">
                    <item.icon size={20} />
                  </span>
                  <h3 className="mt-6 font-display text-lg font-semibold text-white">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{item.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-slate-800/60 bg-navy py-24 sm:py-32" data-testid="swdev-how-we-build">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>How We Build</Eyebrow>
            <h2 className="mt-4 max-w-2xl font-display text-2xl font-semibold tracking-tight text-white sm:text-3xl lg:text-4xl">
              Engineering discipline at every layer.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SECTIONS.map((sec, i) => (
              <Reveal key={sec.title} delay={Math.min(i * 0.06, 0.4)} y={20}>
                <div className="h-full rounded-2xl border border-slate-800/80 bg-void/60 p-7" data-testid={`swdev-section-${i}`}>
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-blue-500/25 bg-blue-500/10 text-blue-400">
                    <sec.icon size={20} />
                  </span>
                  <h3 className="mt-6 font-display text-lg font-semibold text-white">{sec.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{sec.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="hero-radial relative overflow-hidden py-28 sm:py-36" data-testid="swdev-cta">
        <div className="bg-grid absolute inset-0 opacity-50 [mask-image:radial-gradient(ellipse_at_center,black_20%,transparent_70%)]" />
        <Reveal className="relative mx-auto max-w-4xl px-6 text-center lg:px-10">
          <h2 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
            Tell Us What You Want to Build.
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg">
            Share your idea, your workflow, or the system that's holding your
            business back. We'll help you shape it into a buildable plan.
          </p>
          <Link
            to="/contact"
            data-testid="swdev-start-project-btn"
            className="group mt-10 inline-flex items-center gap-2 rounded-full bg-blue-600 px-8 py-4 text-sm font-semibold text-white shadow-[0_0_36px_rgba(59,130,246,0.45)] transition-[background-color,box-shadow,transform] duration-300 hover:bg-blue-500 active:scale-[0.98]"
          >
            Start a Project
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>
    </>
  );
}
